import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AudioProvider } from "../contexts/Audio";
import TextareaListen from "../components/Pronunciation/TextareaListen";
import RecordingButton from "../components/Pronunciation/RecordingButton";
import AudioVisualizer from "../components/Pronunciation/AudioVisualizer";
import { pollStatus } from "../pronunciation";
import logoIcon from "../assets/images/logo-icon.svg";

const CONSENT_KEY = "aussprachetrainer_embed_consent";

const EmbedPage = () => {
    const { t, i18n } = useTranslation();
    const [text, setText] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState("de-DE");
    const [hasConsent, setHasConsent] = useState(false);

    // Recording states
    const [currentlyRecording, setCurrentlyRecording] = useState(false);
    const [currentlyListening, setCurrentlyListening] = useState(false);
    const [recordingState, setRecordingState] = useState("idle");
    const [celeryTaskId, setCeleryTaskId] = useState(null);
    const [audioDuration, setAudioDuration] = useState(null);
    const [recordingBlob, setRecordingBlob] = useState(null);
    const [canvasImage, setCanvasImage] = useState(null);
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
    const [pixelsPerSecond, setPixelsPerSecond] = useState(null);
    const [canvasForColoring, setCanvasForColoring] = useState(null);
    const [understoodText, setUnderstoodText] = useState("");
    const [scores, setScores] = useState(null);
    const [playheadX, setPlayheadX] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const canvasRef = useRef(null);
    const drawingWidthRef = useRef(0);
    const audioRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const isProcessingRef = useRef(false);
    const intervalRef = useRef(null);

    const [originUrl, setOriginUrl] = useState(document.referrer);

    // Read URL parameters
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlText = params.get("text");
        const urlLang = params.get("lang");
        const urlOrigin =
            params.get("origin_url") || params.get("origin") || params.get("referrer");

        if (urlText) setText(decodeURIComponent(urlText));
        if (urlLang) {
            setSelectedLanguage(urlLang);
            // Set i18n language based on lang code
            const langCode = urlLang.split("-")[0]; // Extract 'de' from 'de-DE'
            i18n.changeLanguage(langCode);
        }
        let detectedOrigin = "";
        if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
            // 1. Try ancestorOrigins (most reliable for iframes)
            detectedOrigin =
                window.location.ancestorOrigins[window.location.ancestorOrigins.length - 1];
        } else if (document.referrer) {
            // 2. Try document.referrer
            detectedOrigin = document.referrer;
        } else if (urlOrigin) {
            // 3. Fallback to query param
            detectedOrigin = decodeURIComponent(urlOrigin);
        }

        if (detectedOrigin) {
            setOriginUrl(detectedOrigin);
        }

        // Check for stored consent
        const storedConsent = localStorage.getItem(CONSENT_KEY);
        if (storedConsent === "true") {
            setHasConsent(true);
        }
    }, [i18n]);

    // Handle consent acceptance
    const handleConsentAccept = () => {
        localStorage.setItem(CONSENT_KEY, "true");
        setHasConsent(true);
    };

    // Container width tracking for playhead
    // Container width tracking for playhead
    useEffect(() => {
        if (!containerRef.current) return;

        // Initial set
        setContainerWidth(containerRef.current.offsetWidth);

        const resizeObserver = new ResizeObserver(() => {
            setContainerWidth(containerRef.current.offsetWidth);
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [recordingState, canvasImage]);

    // Audio playback
    const audioUrl = useMemo(() => {
        return recordingBlob ? URL.createObjectURL(recordingBlob) : null;
    }, [recordingBlob]);

    useEffect(() => {
        return () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const togglePlay = async () => {
        const el = audioRef.current;
        if (!el) return;

        if (isPlaying) {
            el.pause();
            return;
        }

        if (el.readyState < 1) {
            await new Promise((resolve) => {
                el.addEventListener("loadedmetadata", resolve, { once: true });
            });
        }

        try {
            await el.play();
        } catch (err) {
            console.error("Audio play failed:", err);
        }
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    // Playhead animation
    useEffect(() => {
        let rafId;
        const el = audioRef.current;
        if (!el) return;

        const update = () => {
            if (!audioDuration || !containerWidth) {
                rafId = requestAnimationFrame(update);
                return;
            }
            const ratio = (el.currentTime * 1000) / audioDuration;
            const x = ratio * containerWidth;
            setPlayheadX(x);
            rafId = requestAnimationFrame(update);
        };

        if (isPlaying) {
            rafId = requestAnimationFrame(update);
        }

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isPlaying, audioDuration, containerWidth]);

    // Canvas image generation when analyzing
    useEffect(() => {
        if (recordingState === "analyzing" && canvasRef.current) {
            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");
            const actualWidth = drawingWidthRef.current;
            tempCanvas.width = actualWidth;
            tempCanvas.height = canvasRef.current.height;
            setCanvasForColoring(tempCanvas);
            setPixelsPerSecond(tempCanvas.width / audioDuration);
            tempCtx.fillRect(0, 0, actualWidth, tempCanvas.height);
            tempCtx.drawImage(
                canvasRef.current,
                0,
                0,
                actualWidth,
                canvasRef.current.height,
                0,
                0,
                actualWidth,
                tempCanvas.height,
            );

            const recordingDataURL = tempCanvas.toDataURL("image/png");
            setCanvasImage(recordingDataURL);
            setCanvasDimensions({
                width: drawingWidthRef.current,
                height: canvasRef.current.height,
            });
        }
    }, [recordingState, audioDuration]);

    // Polling results
    useEffect(() => {
        if (!celeryTaskId) return;

        isProcessingRef.current = false;
        let isMounted = true;

        const colorCanvas = (offsets) => {
            if (!canvasForColoring || !pixelsPerSecond || isProcessingRef.current || !isMounted) {
                return;
            }

            isProcessingRef.current = true;
            const ctx = canvasForColoring.getContext("2d");

            offsets.forEach((offset) => {
                const percentage = offset[2];
                if (percentage < 60) {
                    ctx.fillStyle = "rgba(255, 0, 0, 0.5)";
                } else if (percentage < 87) {
                    ctx.fillStyle = "rgba(255, 255, 0, 0.5)";
                } else {
                    ctx.fillStyle = "rgba(0, 255, 0, 0.5)";
                }

                ctx.fillRect(
                    offset[0] * pixelsPerSecond,
                    0,
                    offset[1] * pixelsPerSecond,
                    canvasForColoring.height,
                );
            });

            setCanvasImage(canvasForColoring.toDataURL("image/png"));
        };

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(async () => {
            try {
                const status = await pollStatus(celeryTaskId);

                if (!isMounted) return;

                if (status.status === "SUCCESS") {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    if (!isProcessingRef.current) {
                        setRecordingState("result");
                        colorCanvas(status.result[1]);
                        setUnderstoodText(status.result[2]);
                        setScores(status.result[0].Paragraph);
                    }
                } else if (status.status === "FAILURE") {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    alert(t("alert.analysis-failed"));
                    handleReset();
                }
            } catch (error) {
                console.error("Failed to fetch status", error);
                if (isMounted && intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        }, 500);

        return () => {
            isMounted = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [celeryTaskId, audioDuration, canvasForColoring, pixelsPerSecond, t]);

    const handleReset = () => {
        setRecordingState("idle");
        setCeleryTaskId(null);
        setIsPlaying(false);
        setPlayheadX(0);
        setUnderstoodText("");
        setScores(null);
        setCanvasImage(null);
        setRecordingBlob(null);
        isProcessingRef.current = false;
    };

    const handleCanvasClick = (e) => {
        const container = containerRef.current;
        const audio = audioRef.current;
        if (!container || !audio || !audioDuration) return;

        const rect = container.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / container.clientWidth));
        const newTime = (ratio * audioDuration) / 1000;

        audio.currentTime = newTime;
        setPlayheadX(clickX);
    };

    const getScaledHeight = () => {
        if (!canvasDimensions.width || !canvasDimensions.height) return "auto";
        const aspectRatio = canvasDimensions.width / canvasDimensions.height;
        const maxDisplayWidth = Math.min(800, window.innerWidth - 40);
        return `${maxDisplayWidth / aspectRatio}px`;
    };

    return (
        <AudioProvider>
            <div className="min-h-screen bg-white p-4 md:p-8">
                <div className="max-w-3xl mx-auto">
                    {!hasConsent ? (
                        /* ── Consent Landing Screen ── */
                        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                            <a
                                href="https://aussprachetrainer.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 mb-4 md:mb-8"
                            >
                                <img
                                    alt="AusspracheTrainer Logo"
                                    className="h-12 w-12"
                                    src={logoIcon}
                                />
                                <span className="text-xl font-medium tracking-normal">
                                    AusspracheTrainer
                                </span>
                            </a>

                            <div className="w-full max-w-md p-4 md:p-6 border border-gray-200 rounded-xl bg-gray-50 text-center">
                                <p className="text-sm text-gray-700 leading-relaxed mb-5">
                                    {t("embed.privacyConsentPart1")}{" "}
                                    <a
                                        href="/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary underline hover:text-purple-600"
                                    >
                                        {t("embed.privacyConsentLink")}
                                    </a>{" "}
                                    {t("embed.privacyConsentPart2")}
                                </p>

                                <button
                                    onClick={handleConsentAccept}
                                    className="w-full py-2.5 px-4 bg-primary text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
                                >
                                    {t("embed.accept")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ── Main Content (after consent) ── */
                        <>
                            {/* Header with text */}
                            <div className="mb-3">
                                <p className="text-sm text-gray-600 mb-2">
                                    {t("embed.practiceText")}
                                </p>
                                <div className="w-fit max-w-[calc(100%_-_0.5rem)] md:max-w-full p-4 mr-2 md:mr-0 border border-border rounded-lg bg-secondary">
                                    <p className="text-2xl font-medium text-gray-800">{text}</p>
                                </div>
                            </div>

                            {/* Listen Button */}
                            <div className="mb-4">
                                <TextareaListen
                                    text={text}
                                    selectedLanguage={selectedLanguage}
                                    currentlyListening={currentlyListening}
                                    setCurrentlyListening={setCurrentlyListening}
                                    setTextareaHasError={() => {}}
                                />
                            </div>

                            {/* Recording Section */}
                            {(recordingState === "idle" || recordingState === "recording") && (
                                <div className="flex flex-col items-center justify-center md:py-8">
                                    {recordingState === "idle" && (
                                        <p className="text-sm text-gray-600 mb-4">
                                            {t("textinstruction.click-to-record")}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-center space-x-4">
                                        <RecordingButton
                                            currentlyRecording={currentlyRecording}
                                            setCurrentlyRecording={setCurrentlyRecording}
                                            setRecordingState={setRecordingState}
                                            text={text}
                                            selectedLanguage={selectedLanguage}
                                            setCeleryTaskId={setCeleryTaskId}
                                            setAudioDuration={setAudioDuration}
                                            setRecordingBlob={setRecordingBlob}
                                            setTextareaHasError={() => {}}
                                            skipPermissionCard={true}
                                            originUrl={originUrl}
                                        />
                                        {recordingState === "recording" && (
                                            <div className="absolute -translate-x-[calc(50%+4.5rem)] md:-translate-x-[calc(50%+5rem-1px)] self-start">
                                                <AudioVisualizer
                                                    externalOffscreenCanvasRef={canvasRef}
                                                    drawingWidthRef={drawingWidthRef}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Results Section */}
                            {(recordingState === "analyzing" || recordingState === "result") && (
                                <div className="flex flex-col items-center space-y-4 w-full">
                                    {recordingBlob && (
                                        <audio
                                            ref={audioRef}
                                            src={audioUrl ?? undefined}
                                            onPlay={onPlay}
                                            onPause={onPause}
                                            onEnded={onEnded}
                                            preload="metadata"
                                            className="hidden"
                                        />
                                    )}
                                    {canvasImage ? (
                                        <div className="w-full">
                                            {understoodText && (
                                                <p className="mb-2">{t("what-you-said")}</p>
                                            )}
                                            <div className="flex flex-col sm:flex-row gap-8">
                                                <div className="w-full max-w-lg min-h-36 h-36 rounded-xl bg-secondary border border-border overflow-hidden">
                                                    {understoodText && scores ? (
                                                        <div className="flex flex-col h-full">
                                                            <p className="flex-1 p-4 font-medium">
                                                                {understoodText}
                                                            </p>
                                                            <div className="flex h-8 text-xs font-medium text-white gap-px bg-purple-600">
                                                                <div className="bg-primary flex-1 flex items-center justify-center text-center">
                                                                    {t("scores.accuracy") +
                                                                        ": " +
                                                                        Math.ceil(
                                                                            scores.accuracy_score,
                                                                        )}
                                                                </div>
                                                                <div className="bg-primary flex-1 flex items-center justify-center text-center">
                                                                    {t("scores.completeness") +
                                                                        ": " +
                                                                        Math.ceil(
                                                                            scores.completeness_score,
                                                                        )}
                                                                </div>
                                                                <div className="bg-primary flex-1 flex items-center justify-center text-center">
                                                                    {t("scores.fluency") +
                                                                        ": " +
                                                                        Math.ceil(
                                                                            scores.fluency_score,
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-4 items-center justify-center h-full">
                                                            <div role="status">
                                                                <svg
                                                                    aria-hidden="true"
                                                                    className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary"
                                                                    viewBox="0 0 100 101"
                                                                    fill="none"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                                        fill="currentColor"
                                                                    />
                                                                    <path
                                                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                                        fill="currentFill"
                                                                    />
                                                                </svg>
                                                                <span className="sr-only">
                                                                    {t("analysis-loading")}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm italic text-gray-600 px-4 text-center">
                                                                    {t("tooltip-loading-analysis")}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center">
                                                    <button
                                                        className="p-px text-white rounded-full bg-primary hover:bg-purple-500"
                                                        onClick={togglePlay}
                                                    >
                                                        {isPlaying ? (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                                className="size-5"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                                className="size-5"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    <div
                                                        className="max-w-full overflow-x-auto relative cursor-pointer"
                                                        ref={containerRef}
                                                        onClick={handleCanvasClick}
                                                    >
                                                        <img
                                                            src={canvasImage}
                                                            alt="Audio visualization"
                                                            className="rounded-lg"
                                                            style={{
                                                                maxWidth: "100%",
                                                                width: "100%",
                                                                maxHeight: getScaledHeight(),
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                        <div
                                                            data-playhead
                                                            className="top-0 absolute w-px h-full bg-red-500 pointer-events-none"
                                                            style={{
                                                                transform: `translateX(${playheadX}px)`,
                                                                willChange: "transform",
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {scores && (
                                                <div className="flex justify-start gap-2 w-full mt-4">
                                                    <button
                                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-purple-600"
                                                        onClick={handleReset}
                                                    >
                                                        <div className="flex flex-row gap-1 items-center">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                strokeWidth={2}
                                                                stroke="currentColor"
                                                                className="size-4"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                                                />
                                                            </svg>
                                                            <p className="text-sm">
                                                                {t("embed.tryAgain")}
                                                            </p>
                                                        </div>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-gray-100 rounded-lg">
                                            No audio visualization available
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </AudioProvider>
    );
};

export default EmbedPage;
